import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import WolfMark from "@/components/WolfMark";
import { useAuth } from "@/context/AuthContext";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/music", label: "Music" },
  { to: "/merch", label: "Merch" },
  { to: "/den", label: "The Den" },
  { to: "/fire", label: "Fire Pages" },
  { to: "/about", label: "About" },
  { to: "/media", label: "Media" },
  { to: "/wolf-dawg", label: "WOLF DAWG" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <span className="inline-flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 border border-[#D4AF37]/70 bg-black/40">
            <WolfMark className="w-7 h-7 sm:w-8 sm:h-8" />
          </span>
          <span className="font-display text-2xl tracking-[0.4em] text-[#D4AF37] group-hover:text-[#F4C542] transition-colors">
            WOLF
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.28em] font-medium transition-colors ${
                  isActive ? "text-white" : "text-white/55 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {user && user.id ? (
            <>
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/60" data-testid="nav-user-name">
                {user.name}
              </span>
              <button
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="text-[11px] uppercase tracking-[0.28em] text-white/70 hover:text-white border border-white/15 hover:border-white/40 px-4 py-2 transition-all"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-[11px] uppercase tracking-[0.28em] text-white/70 hover:text-white transition-colors"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                data-testid="nav-join-link"
                className="text-[11px] uppercase tracking-[0.28em] text-white bg-[#7A5A18]/20 border border-[#D4AF37]/40 hover:bg-[#7A5A18]/40 hover:border-[#D4AF37] px-4 py-2 transition-all"
              >
                Join The Pack
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#D4AF37]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden absolute top-full inset-x-0 bg-black/95 border-b border-white/10 backdrop-blur-xl"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-display text-2xl tracking-wider ${isActive ? "text-[#D4AF37]" : "text-white"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-white/10 flex flex-col gap-3">
              {user && user.id ? (
                <button
                  data-testid="mobile-nav-logout"
                  onClick={async () => { setOpen(false); await logout(); nav("/"); }}
                  className="text-xs uppercase tracking-[0.28em] text-white/70 text-left"
                >
                  Sign out — {user.name}
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="mobile-nav-login"
                    onClick={() => setOpen(false)}
                    className="text-xs uppercase tracking-[0.28em] text-white/70"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    data-testid="mobile-nav-join"
                    onClick={() => setOpen(false)}
                    className="text-xs uppercase tracking-[0.28em] text-white border border-[#D4AF37]/40 px-4 py-2 inline-block w-fit"
                  >
                    Join The Pack
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
